import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, boutiqueOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, FileText, CheckCircle2, AlertCircle, Trash2, RefreshCcw, X } from "lucide-react";
import { toast } from "sonner";
import "./ZenotiImportDialog.css";

const ABSENCE_STATUSES = ["Call-Out (unexcused)", "Call-Out (excused)", "PTO", "Bereavement", "Jury Duty", "Vacation", "Personal Time"];
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SCHEDULE_FILE_PATTERN = /\.(pdf|csv|xlsx|xlsm|xls)$/i;
const REGULAR_CROSS_CENTER_THERAPISTS = {
  PLT: {
    josh: { employee_id: "cross-center:plt:josh-walder", employee_name: "Josh Walder", role: "massage_therapist" },
    joshwalder: { employee_id: "cross-center:plt:josh-walder", employee_name: "Josh Walder", role: "massage_therapist" },
    karin: { employee_id: "cross-center:plt:karin-whyte", employee_name: "Karin Whyte", role: "massage_therapist" },
    karinwhyte: { employee_id: "cross-center:plt:karin-whyte", employee_name: "Karin Whyte", role: "massage_therapist" },
  },
};

function normalizeRegularCrossCenter(preview, boutique) {
  return { ...preview, employees: (preview?.employees || []).map((employee) => {
    if (employee.matched) return employee;
    const key = String(employee.raw_name || "").toLowerCase().replace(/[^a-z]/g, "");
    const profile = REGULAR_CROSS_CENTER_THERAPISTS[boutique]?.[key];
    return profile ? { ...employee, ...profile, matched: true, cross_center: true } : employee;
  }) };
}

// The browser keeps a small local copy of a successfully committed import.
// This is deliberately the whole matched roster, not only cross-centre staff:
// the Journey view must not become empty while the local API rebuilds its
// derived daily log after an import.
function importedScheduleCacheKey(boutique, weekStart) { return `toto.imported-schedule.${boutique}.${weekStart}`; }

function fmtDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}
function fmtDateTime(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

function importErrorMessage(error, fallback) {
  const body = error?.response?.data;
  const detail = body?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || String(item)).join(" ");
  if (typeof detail === "string") return detail;
  if (typeof body === "string" && body.trim()) return body;
  if (error?.code === "ERR_NETWORK") return "The schedule service could not be reached. Refresh the page and try again.";
  if (error?.response?.status >= 500) return "The schedule service could not finish reading this file. Please try it once more.";
  return fallback;
}

/**
 * Full-screen dialog embedding the Zenoti schedule importer.
 * Used on the Daily page — no separate route needed.
 */
export default function ZenotiImportDialog({ open, onClose, boutique: initialBoutique, onImported, initialPreview }) {
  const { user } = useAuth();
  const [boutique, setBoutique] = useState(initialBoutique || user?.boutique_code || "PLT");
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);

  const canManage = user && (user.role === "owner" || user.role === "recruiter" || (user.role === "boutique_manager" && user.boutique_code === boutique));

  useEffect(() => { if (initialBoutique) setBoutique(initialBoutique); }, [initialBoutique]);

  const loadRecent = useCallback(() => {
    api.get("/schedules/zenoti/recent", { params: { boutique } })
      .then((r) => setRecent(r.data || []))
      .catch(() => setRecent([]));
  }, [boutique]);
  useEffect(() => { if (open) loadRecent(); }, [open, loadRecent]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const dialog = document.querySelector('[data-testid="zenoti-dialog"] [role="dialog"]');
    const controls = () => [...dialog.querySelectorAll('button:not(:disabled),select:not(:disabled),input:not([type="hidden"]):not(:disabled),[tabindex="0"]')].filter(element => element.getClientRects().length);
    controls()[0]?.focus();
    const keydown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); onClose(); }
      if (event.key === "Tab") {
        const items = controls();
        const first = items[0], last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }
    };
    dialog?.addEventListener("keydown", keydown);
    return () => { dialog?.removeEventListener("keydown", keydown); if (previous?.isConnected) previous.focus(); };
  }, [open, onClose]);

  useEffect(() => { if (open && initialPreview) setPreview(initialPreview); }, [open, initialPreview]);

  // Reset preview state whenever the dialog closes
  useEffect(() => {
    if (!open) {
      setPreview(null); setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [open]);

  const doUpload = async (f) => {
    if (!f) return;
    if (!canManage) { toast.error("Only this boutique's GM can import a schedule."); return; }
    if (!SCHEDULE_FILE_PATTERN.test(f.name || "")) {
      toast.error("Choose a Zenoti PDF, CSV, or Excel schedule (.xls or .xlsx).");
      return;
    }
    setBusy(true);
    setFile(f);
    const fd = new FormData();
    fd.append("boutique_code", boutique);
    fd.append("file", f);
    try {
      // Let the browser supply the multipart boundary. Supplying a bare
      // Content-Type breaks FastAPI's file parser in some Safari/WebKit paths.
      const r = await api.post("/schedules/zenoti/preview", fd);
      const normalized = normalizeRegularCrossCenter(r.data, boutique);
      const saved = await api.get(`/schedules/zenoti/${boutique}/${normalized.week_start}`).catch(error => {
        if (error?.response?.status === 404) return {data: {}};
        throw error;
      });
      normalized.employees = normalized.employees.map(employee => ({...employee, absence_statuses: Object.fromEntries(Object.entries(saved.data.absence_records?.[employee.employee_id] || {}).filter(([day]) => normalized.days.includes(day)).map(([day, record]) => [day, record.status]))}));
      setPreview(normalized);
      if ((normalized.warnings || []).length) toast.warning(normalized.warnings[0]);
      else if (!normalized.employees?.length) toast.error("Couldn't detect any shifts in this file.");
      else toast.success(`Parsed ${normalized.employees.length} teammates for the week of ${fmtDate(normalized.week_start)}`);
    } catch (e) {
      toast.error(importErrorMessage(e, "The schedule could not be read. Try the Zenoti Day View PDF, CSV, or Excel export."));
      setPreview(null);
    } finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const setScheduleOnlyAcceptance = (index, updates) => {
    setPreview((current) => current ? {
      ...current,
      employees: current.employees.map((employee, employeeIndex) => employeeIndex === index ? { ...employee, ...updates } : employee),
    } : current);
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const payload = {
        boutique_code: boutique,
        collection_job_id: preview.collection_job_id || null,
        week_start: preview.week_start,
        days: preview.days,
        source_filename: preview.source_filename,
        mode: preview.mode || "weekly",
        employees: preview.employees.map((e) => ({
          employee_id: e.employee_id,
          employee_name: e.employee_name,
          raw_name: e.raw_name,
          matched: !!e.matched,
          cross_center: !!e.cross_center,
          role: e.role || null,
          shifts: e.shifts || {},
          absence_statuses: e.absence_statuses || {},
          zenoti_id: e.zenoti_id || null,
          accepted_schedule_only: Boolean(e.accepted_schedule_only),
          schedule_only_role: e.schedule_only_role || null,
        })),
      };
      const r = await api.post("/schedules/zenoti/commit", payload);
      const committedEmployees = payload.employees.filter((employee) => employee.matched && Object.keys(employee.shifts || {}).length);
      if (committedEmployees.length) {
        const key = importedScheduleCacheKey(boutique, preview.week_start);
        const existing = JSON.parse(window.localStorage.getItem(key) || "[]");
        const merged = [
          ...existing.filter((employee) => !committedEmployees.some((next) => next.employee_id === employee.employee_id)),
          ...committedEmployees,
        ];
        window.localStorage.setItem(key, JSON.stringify(merged));
      }
      const label = payload.mode === "daily" ? "today's shifts" : `${r.data.employees_saved} shifts`;
      toast.success(`Saved ${label} for week of ${fmtDate(preview.week_start)}`);
      setPreview(null); setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      loadRecent();
      onImported?.();
    } catch (e) {
      toast.error(importErrorMessage(e, "The schedule preview could not be saved."));
    } finally { setBusy(false); }
  };

  const deleteWeek = async (weekStart) => {
    if (!window.confirm(`Delete the import for week of ${fmtDate(weekStart)}?`)) return;
    try {
      await api.delete(`/schedules/zenoti/${boutique}/${weekStart}`);
      toast.success("Removed");
      loadRecent();
      onImported?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!open) return null;
  const matched = preview?.employees?.filter((e) => e.matched)?.length || 0;
  const scheduleOnlyAccepted = preview?.employees?.filter((e) => e.accepted_schedule_only)?.length || 0;
  const unmatched = preview?.employees?.filter((e) => !e.matched && !e.accepted_schedule_only) || [];

  return (
    <div className="zenoti-import-dialog fixed inset-0 z-50 flex items-start justify-center p-6 overflow-y-auto mobile-modal-wrap" data-testid="zenoti-dialog">
      <div role="dialog" aria-modal="true" aria-labelledby="zenoti-import-title" className="bg-white rounded-3xl w-full max-w-5xl card-shadow my-8 mobile-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-8 py-5 border-b border-stone-100 flex items-center justify-between">
          <div>
            <h1 id="zenoti-import-title" className="font-serif-display text-2xl text-stone-900 mt-1">Import Schedule</h1>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={boutique}
              onChange={(e) => setBoutique(e.target.value)}
              aria-label="Boutique" data-testid="zenoti-boutique"
              className="px-4 py-2 rounded-full subtle-border text-sm outline-none bg-white"
            >
              {["PLT", "FLL", "WYN", "PNC"].map((b) => <option key={b} value={b}>{boutiqueOf(b).short}</option>)}
            </select>
            <button onClick={loadRecent} className="p-2 rounded-full hover:bg-stone-100" title="Refresh">
              <RefreshCcw size={16} strokeWidth={1.25} className="text-stone-500" />
            </button>
            <button onClick={onClose} aria-label="Close import" data-testid="zenoti-dialog-close" className="p-2 rounded-full hover:bg-stone-100">
              <X size={18} strokeWidth={1.25} className="text-stone-500" />
            </button>
          </div>
        </div>

        <div className="zenoti-import-dialog__body p-8 space-y-6">
          {/* Dropzone */}
          {!preview && (
            <section
              onDrop={onDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              role="button" tabIndex={canManage ? 0 : -1} aria-label="Choose a Zenoti schedule file" onKeyDown={(event) => { if (canManage && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); inputRef.current?.click(); } }} data-testid="zenoti-dropzone"
              className={`rounded-3xl border-2 border-dashed transition-all p-14 text-center cursor-pointer ${dragOver ? "border-stone-400 bg-stone-50/70" : "border-stone-200"} ${canManage ? "hover:border-stone-300" : "opacity-60 cursor-not-allowed"}`}
              onClick={() => canManage && inputRef.current?.click()}
            >
              <input
                ref={inputRef} type="file" accept=".pdf,.csv,.xls,.xlsx,.xlsm" className="hidden"
                data-testid="zenoti-file-input"
                onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])}
              />
              <div className="w-14 h-14 rounded-full bg-stone-50 border border-stone-100 mx-auto flex items-center justify-center mb-4">
                <Upload size={20} strokeWidth={1.25} className="text-stone-500" />
              </div>
              <div className="font-serif-display text-xl text-stone-800">
                {busy ? "Reading your file…" : "Drop your Zenoti schedule here"}
              </div>
              <div className="text-xs text-stone-400 mt-2">or click to browse — PDF, CSV or Excel</div>
              {!canManage && <div className="text-xs text-amber-600 mt-4">Read-only for your role. Ask a GM or Owner to run the import.</div>}
            </section>
          )}

          {/* Preview */}
          {preview && (
            <section className="space-y-4" data-testid="zenoti-preview">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="label-eyebrow">Preview · {file?.name || preview.source_filename}</div>
                  <div className="font-serif-display text-xl text-stone-900 mt-1 flex items-center gap-3">
                    Week of {fmtDate(preview.week_start)}
                    <span className={`text-[10px] font-sans px-2 py-0.5 rounded-full uppercase tracking-wider ${preview.mode === "daily" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-stone-100 text-stone-500"}`} data-testid="preview-mode">
                      {preview.mode === "daily" ? "Daily export" : "Weekly export"}
                    </span>
                  </div>
                  <div className="text-xs text-stone-500 mt-1">
                    <span className="text-emerald-700">{matched} matched</span>
                    {scheduleOnlyAccepted > 0 && <span className="text-emerald-700"> · {scheduleOnlyAccepted} schedule-only accepted</span>}
                    {unmatched.length > 0 && <span className="text-amber-700"> · {unmatched.length} unmatched</span>}
                    {preview.mode === "daily" && preview.date && <span className="text-stone-500"> · {fmtDate(preview.date)} only — merges into the week</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setPreview(null); setFile(null); if (inputRef.current) inputRef.current.value = ""; }} className="px-4 py-2 rounded-full text-sm text-stone-600 hover:bg-stone-100" data-testid="zenoti-cancel">Cancel</button>
                  <button onClick={commit} disabled={busy || matched + scheduleOnlyAccepted === 0} className="px-5 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40" data-testid="zenoti-commit">
                    <CheckCircle2 size={14} strokeWidth={1.5} className="inline mr-1" />
                    {busy ? "Saving…" : `Commit ${matched + scheduleOnlyAccepted} shifts`}
                  </button>
                </div>
              </div>

              {(preview.warnings || []).map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50/70 border border-amber-100 rounded-2xl px-4 py-3">
                  <AlertCircle size={14} strokeWidth={1.5} className="mt-0.5" />
                  <span>{w}</span>
                </div>
              ))}

              <div className="zenoti-import-dialog__grid bg-white subtle-border rounded-2xl" role="region" aria-label="Schedule preview — scroll to review teammates and days" tabIndex={0}>
                <div className="zenoti-import-dialog__table">
                  <div className="zenoti-import-dialog__dates grid grid-cols-9 border-b border-stone-100 text-[10px] label-eyebrow">
                    <div className="px-4 py-3 col-span-2">Teammate (from Zenoti)</div>
                    {preview.days.map((iso, i) => (
                      <div key={iso} className="px-2 py-3 text-center">
                        <div>{DAYS_SHORT[i]}</div>
                        <div className="text-[9px] text-stone-400 mt-0.5">{fmtDate(iso)}</div>
                      </div>
                    ))}
                  </div>
                  {preview.employees.map((e, idx) => (
                    <div key={idx} className={`grid grid-cols-9 border-b border-stone-50 items-center ${!e.matched && !e.accepted_schedule_only ? "bg-amber-50/40" : ""}`} data-testid={`zenoti-row-${idx}`}>
                      <div className="px-4 py-3 col-span-2">
                        <div className="text-sm text-stone-800 flex items-center gap-2">
                          {e.matched || e.accepted_schedule_only ? <CheckCircle2 size={12} strokeWidth={1.75} className="text-emerald-600" /> : <AlertCircle size={12} strokeWidth={1.75} className="text-amber-600" />}
                          {e.employee_name || e.raw_name}
                        </div>
                        <div className="text-[10px] text-stone-400">
                          {e.matched ? (e.cross_center ? "Cross-center coverage" : (e.title || e.role || "").replace(/_/g, " ")) : e.accepted_schedule_only ? `Schedule-only coverage · ${(e.schedule_only_role || "").replace(/_/g, " ")}` : "Not in the company roster"}
                        </div>
                        {!e.matched && <div className="zenoti-import-dialog__exception">
                          <select value={e.schedule_only_role || ""} onChange={(event) => setScheduleOnlyAcceptance(idx, { schedule_only_role: event.target.value })} aria-label={`Operational role for ${e.raw_name || e.employee_name}`}>
                            <option value="">Choose operational role</option><option value="massage_therapist">Massage therapist</option><option value="front_desk">Experience Guide</option><option value="boutique_manager">Boutique manager</option>
                          </select>
                          <button type="button" onClick={() => setScheduleOnlyAcceptance(idx, { accepted_schedule_only: !e.accepted_schedule_only })} disabled={!e.accepted_schedule_only && !e.schedule_only_role}>
                            {e.accepted_schedule_only ? "Accepted for this schedule" : "Accept schedule only"}
                          </button>
                        </div>}
                      </div>
                      {preview.days.map((iso) => {
                        const val = e.shifts?.[iso] || "";
                        const isOff = val.toUpperCase() === "OFF";
                        return (
                          <div key={iso} className="px-2 py-2 text-center">
                            <div className={`text-[11px] ${isOff ? "text-stone-300 italic" : "text-stone-700"}`}>
                              {val || "—"}
                            </div>
                            {e.matched && (val || e.absence_statuses?.[iso]) && <select className="zenoti-absence-select" aria-label={`Schedule status for ${e.employee_name || e.raw_name} on ${iso}`} disabled={busy || !canManage} value={e.absence_statuses?.[iso] || ""} onChange={event => setScheduleOnlyAcceptance(idx, {absence_statuses: {...e.absence_statuses, [iso]: event.target.value}})}>
                              <option value="">Scheduled</option>
                              {ABSENCE_STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                            </select>}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {preview.employees.length === 0 && (
                    <div className="p-10 text-center text-sm text-stone-400 italic">
                      Couldn't find any shift rows in this file. Make sure your Zenoti export includes a row per employee.
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Recent imports */}
          <section data-testid="zenoti-recent">
            <h2 className="zenoti-import-dialog__recent-title">Recent Imports</h2>
            {recent.length === 0 && <div className="text-sm text-stone-400 italic">Nothing imported yet — your first upload will show here.</div>}
            <div className="space-y-2">
              {recent.map((r) => (
                <div key={r.week_start} className="subtle-border rounded-2xl px-5 py-3 flex items-center justify-between gap-4" data-testid={`zenoti-recent-${r.week_start}`}>
                  <div className="flex items-center gap-3">
                    <FileText size={16} strokeWidth={1.25} className="text-stone-400" />
                    <div>
                      <div className="text-sm text-stone-800">Week of {fmtDate(r.week_start)}</div>
                      <div className="text-[11px] text-stone-400">
                        {r.employees_matched || 0} teammates · {r.source_filename || "upload"} · imported {fmtDateTime(r.imported_at)}
                        {r.imported_by_name ? ` by ${r.imported_by_name}` : ""}
                      </div>
                    </div>
                  </div>
                  {canManage && (
                    <button onClick={() => deleteWeek(r.week_start)} className="p-2 rounded-full hover:bg-stone-100 text-stone-400" title="Delete import">
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
